import { Head, Link } from "@inertiajs/react"
import { useState } from "react"
import { ArrowLeft, ArrowRight, ListOrdered, Lock } from "lucide-react"
import { MemberLayout } from "../components/MemberLayout"
import { LessonBody } from "../Courses/Lessons/LessonBody"
import { TrackProgress, TrackRail } from "@/components/track/TrackRail"
import { Button } from "@/components/ui/button"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer"

// The track player (plan 2026-09-23-002): a guided track's lesson with the
// step list beside it. Laptop: the list stays on the left. Phone: a sticky
// bar with progress and a Steps button that opens the list in a drawer.
export default function TrackLesson({
  currentUser,
  course,
  lesson,
  questions = [],
  resources = [],
  completed,
  quiz = null,
  track,
  currentStep,
  rail,
  previousHref = null,
  next = null,
  certified = false,
}) {
  const [stepsOpen, setStepsOpen] = useState(false)
  const total = rail.steps.length

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title={`${lesson.title} - ${track.title} - Kingdom Factor`} />

      {/* Phone: sticky path bar */}
      <div className="lg:hidden sticky top-14 z-20 -mx-4 mb-4 border-b border-zinc-200 bg-white/95 backdrop-blur px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <Link href={track.overviewHref} className="flex min-h-[44px] items-center truncate text-sm font-semibold text-zinc-900">
              {track.title}
            </Link>
            <p className="text-xs text-zinc-500">
              Step {currentStep} of {total} · {course.title}
            </p>
          </div>
          <Button variant="outline" size="sm" className="min-h-[44px] shrink-0" onClick={() => setStepsOpen(true)}>
            <ListOrdered className="h-4 w-4" />
            Steps
          </Button>
        </div>
        <TrackProgress progress={rail.progress} className="mt-2" />
      </div>

      <Drawer direction="left" open={stepsOpen} onOpenChange={setStepsOpen}>
        <DrawerContent className="h-full w-[85vw] max-w-sm overflow-y-auto bg-zinc-50">
          <DrawerHeader className="sr-only">
            <DrawerTitle>Path steps</DrawerTitle>
          </DrawerHeader>
          <div className="p-4">
            <TrackRail
              track={track}
              steps={rail.steps}
              progress={rail.progress}
              activeLessonId={lesson.id}
              overviewHref={track.overviewHref}
              certified={certified}
              onNavigate={() => setStepsOpen(false)}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <div className="lg:grid lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-10">
        {/* Laptop: the step list stays on screen */}
        <aside className="hidden lg:block">
          <div className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto pr-2 pb-4">
            <TrackRail
              track={track}
              steps={rail.steps}
              progress={rail.progress}
              activeLessonId={lesson.id}
              overviewHref={track.overviewHref}
              certified={certified}
            />
          </div>
        </aside>

        <main className="min-w-0 max-w-3xl space-y-6">
          <p className="hidden lg:block font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            Step {currentStep} of {total} · {course.title}
          </p>

          <LessonBody
            course={course}
            lesson={lesson}
            questions={questions}
            resources={resources}
            completed={completed}
            quiz={quiz}
            trackId={track.id}
            nextStep={next}
          />

          <div className="flex items-center justify-between gap-4 border-t border-zinc-200 pt-4">
            {previousHref ? (
              <Link
                href={previousHref}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-kf-red min-h-[44px] px-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </Link>
            ) : (
              <span />
            )}
            {/* Once the lesson is done, the body offers the next step itself,
                so the footer does not repeat it. */}
            {next &&
              !completed &&
              (next.locked ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-zinc-400 min-h-[44px] px-2" title="Finish this step to open the next one">
                  <Lock className="h-3.5 w-3.5" />
                  Next: {next.title}
                </span>
              ) : (
                <Link
                  href={next.href}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-kf-red min-h-[44px] px-2 text-right"
                >
                  Next: {next.title}
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
              ))}
          </div>
        </main>
      </div>
    </MemberLayout>
  )
}
